import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Cookies from "js-cookie";
import API_ENDPOINTS from "../config/api";
import axios from "axios";
import { formatBuildingLabel } from "../utils/apartmentDisplay";

const {
  KEYS: COOKIE_KEYS,
  EXPIRES: COOKIE_EXPIRES,
  OPTIONS: COOKIE_OPTIONS,
} = API_ENDPOINTS.COOKIE_CONFIG;

// 페이지 애니메이션 변형 설정
const pageVariants = {
  initial: {
    opacity: 0,
    y: 20,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.4, 0, 0.2, 1],
    },
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

// 컨텐츠 애니메이션 변형 설정
const contentVariants = {
  initial: {
    opacity: 0,
    y: 20,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.2,
      duration: 0.5,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

// 버튼 애니메이션 변형 설정
const buttonVariants = {
  initial: {
    opacity: 0,
    y: 20,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.6,
      duration: 0.5,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

function CoOwnerCheckPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [hasCoOwner, setHasCoOwner] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showProcessingModal, setShowProcessingModal] = useState(false);
  const [processingClaimData, setProcessingClaimData] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [showPartialCompleteModal, setShowPartialCompleteModal] =
    useState(false);
  const [partialCompleteData, setPartialCompleteData] = useState(null);

  // URL 파라미터에서 code를 가져와서 소셜 로그인 처리
  const hasProcessedLogin = useRef(false);

  // 모달이 열릴 때 body 스크롤 방지
  useEffect(() => {
    if (showProcessingModal || showCompleteModal || showPartialCompleteModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    // 컴포넌트 언마운트 시 body 스타일 복원
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [showProcessingModal, showCompleteModal, showPartialCompleteModal]);

  useEffect(() => {
    // axios 인터셉터 설정
    API_ENDPOINTS.setupAxiosInterceptors();

    const searchParams = new URLSearchParams(location.search);
    const code = searchParams.get("code");
    const state = searchParams.get("state");

    if (code && state === "kakao_login" && !hasProcessedLogin.current) {
      hasProcessedLogin.current = true;
      handleKakaoSocialLogin(code);
    }
  }, [location.search]);

  // 카카오 소셜 로그인 처리
  const handleKakaoSocialLogin = async (code) => {
    setIsLoading(true);

    try {
      const response = await axios.post(
        `${API_ENDPOINTS.BASE_URL}/mobile/auth/kakao`,
        {
          // redirectUri: "http://localhost:3000/co-owner-check",
          // redirectUri: "http://172.30.1.33:3000/co-owner-check",
          redirectUri: "https://www.theh-agreement.co.kr/co-owner-check",
          code: code,
          // apartmentCode: "테스트",
          apartmentCode:
            Cookies.get("apartmentCode") === "Richmond"
              ? "리치먼드"
              : Cookies.get("apartmentCode") === "Raycounty"
                ? "레이카운티"
                : Cookies.get("apartmentCode") === "Harringtonplace"
                  ? "헤링턴플레이스"
                  : Cookies.get("apartmentCode") === "Singilpark"
                    ? "신길파크자이"
                    : Cookies.get("apartmentCode") === "DMCArtForest"
                      ? "DMC아트포레자이"
                      : Cookies.get("apartmentCode") === "GodeokUrbanBridge"
                        ? "고덕어반브릿지"
                        : Cookies.get("apartmentCode") === "GeomdanUrbanCenter"
                          ? "검단어반센트로"
                          : Cookies.get("apartmentCode") ===
                              "GeumgangPenteriumSeaglo"
                            ? "금강펜테리움더시글로"
                            : Cookies.get("apartmentCode") ===
                                "GeumgangPenteriumSeagloOffice"
                              ? "금강펜테리움더시글로오피스텔"
                              : Cookies.get("apartmentCode") ===
                                  "GangbyeonStarhills"
                                ? "덕소강변스타힐스"
                                : Cookies.get("apartmentCode") ===
                                    "YangjuOkjeongLH"
                                  ? "양주옥정LH엘리프"
                                  : Cookies.get("apartmentCode") ===
                                      "UlsanGwellCityXiComplex1"
                                    ? "울산지웰시티자이1단지"
                                    : Cookies.get("apartmentCode") ===
                                        "YeongheungForestPrugioParkBien"
                                      ? "영흥숲푸르지오파크비엔"
                                      : Cookies.get("apartmentCode") ===
                                          "HillstateYonginGojinStationComplex1"
                                        ? "힐스테이트용인고진역1단지"
                                        : Cookies.get("apartmentCode") ===
                                            "HillstateYonginGojinStationComplex2"
                                          ? "힐스테이트용인고진역2단지"
                                          : Cookies.get("apartmentCode") ===
                                              "HillstateBongdamPrideCity"
                                            ? "힐스테이트봉담프라이드시티"
                                            : Cookies.get("apartmentCode") ===
                                                "PyeongchonIrvineFirstTheSharp"
                                              ? "평촌어바인퍼스트더샵"
                                              : Cookies.get("apartmentCode") ===
                                                  "NaepoNewTownMoaMiraedMegacity2nd"
                                                ? "내포신도시모아미래도메가시티2차"
                                                : Cookies.get("apartmentCode") ===
                                                    "BucheonIllumiState"
                                                  ? "부천일루미스테이트"
                                                  : Cookies.get("apartmentCode") ===
                                                      "SongdoStationSeohaeGrandbleThePark"
                                                    ? "송도역서해그랑블더파크"
                                                    : Cookies.get("apartmentCode") ===
                                                        "BupyeongHowstoryUrbanGreen"
                                                      ? "부평하우스토리어반그린"
                                                      : Cookies.get("apartmentCode") ===
                                                          "ForeonStation5"
                                                        ? "포레온스테이션5"
                                                        : Cookies.get("apartmentCode") ===
                                                            "HyundaiTerraTowerGuriGalmae"
                                                          ? "현대테라타워구리갈매"
                                                          : Cookies.get("apartmentCode") ===
                                                              "테스트"
                                                            ? "테스트"
                                                            : "",
          email: "",
        },
      );

      // 로그인 성공 시 토큰과 사용자 정보를 쿠키에 저장
      if (response.data.accessToken && response.data.refreshToken) {
        // Access Token 저장
        Cookies.set(COOKIE_KEYS.ACCESS_TOKEN, response.data.accessToken, {
          expires: COOKIE_EXPIRES.ACCESS_TOKEN,
          ...COOKIE_OPTIONS,
        });

        // Refresh Token 저장
        Cookies.set(COOKIE_KEYS.REFRESH_TOKEN, response.data.refreshToken, {
          expires: COOKIE_EXPIRES.REFRESH_TOKEN,
          ...COOKIE_OPTIONS,
        });

        // 사용자 정보 저장
        const userInfo = {
          userId: response.data.userId,
          status: response.data.status,
        };

        Cookies.set(COOKIE_KEYS.USER_INFO, JSON.stringify(userInfo), {
          expires: COOKIE_EXPIRES.USER_INFO,
          ...COOKIE_OPTIONS,
        });

        setUserInfo(userInfo);

        // 로그인 성공 후 기존 신청내역 확인
        checkExistingClaim();
      }
    } catch (error) {
      console.error("소셜 로그인 실패:", error);
      // 로그인 실패 시 에러 처리
      setLoginError("로그인에 실패했습니다. 다시 시도해주세요.");
      setShowErrorModal(true);
      setTimeout(() => {
        setShowErrorModal(false);
        navigate("/");
      }, 2000);
    } finally {
      setIsLoading(false);
    }
  };

  // 기존 신청내역 확인
  const checkExistingClaim = async () => {
    try {
      const accessToken = Cookies.get(COOKIE_KEYS.ACCESS_TOKEN);

      if (!accessToken) {
        return;
      }

      const response = await axios.get(
        `${API_ENDPOINTS.BASE_URL}/mobile/claim`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        },
      );

      // 빈 객체가 아니고 claimStatus가 complete인 경우
      if (
        response.data &&
        Object.keys(response.data).length > 0 &&
        response.data.claimStatus === "complete"
      ) {
        setShowCompleteModal(true);
      }
      // processing 상태이지만 신청자 본인은 완료된 경우 (공동신청자 중 일부가 미완료)
      else if (
        response.data &&
        Object.keys(response.data).length > 0 &&
        response.data.claimStatus === "processing" &&
        response.data.claimAgree === true &&
        response.data.claimIdCard === true &&
        response.data.sharedOwner === true &&
        response.data.claimShareList &&
        response.data.claimShareList.length > 0
      ) {
        setPartialCompleteData(response.data);
        setShowPartialCompleteModal(true);
      }
      // processing 상태인 경우 (기존 로직)
      else if (
        response.data &&
        Object.keys(response.data).length > 0 &&
        response.data.claimStatus === "processing"
      ) {
        setProcessingClaimData(response.data);
        setShowProcessingModal(true);
      }
    } catch (error) {
      console.error("기존 신청내역 확인 실패:", error);
      // 에러가 발생해도 계속 진행
    }
  };

  const handleCoOwnerSelection = (value) => {
    setHasCoOwner(value);
  };

  // 신청서 삭제 함수
  const handleDeleteClaim = async () => {
    try {
      const accessToken = Cookies.get(COOKIE_KEYS.ACCESS_TOKEN);

      if (!accessToken) {
        alert("인증 토큰이 없습니다. 다시 로그인해주세요.");
        return;
      }

      await axios.delete(`${API_ENDPOINTS.BASE_URL}/mobile/claim`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      });

      // 삭제 성공 시 모달 닫기
      setShowProcessingModal(false);
      setProcessingClaimData(null);
      // 홈페이지로 리다이렉트
      navigate("/");
    } catch (error) {
      console.error("신청서 삭제 실패:", error);
      alert("신청서 삭제에 실패했습니다. 다시 시도해주세요.");
    }
  };

  const handleNext = async () => {
    if (hasCoOwner === null) {
      alert("공동소유자 여부를 선택해주세요.");
      return;
    }

    try {
      // 쿠키에서 동/호수 정보를 가져오기
      const cookieData = Cookies.get("buildingUnitInfo");
      let buildingUnitInfo = {};
      if (cookieData) {
        try {
          buildingUnitInfo = JSON.parse(cookieData);
        } catch (error) {
          console.error("쿠키 파싱 오류:", error);
        }
      }

      // /mobile/claim API 호출
      const claimData = {
        sharedOwner: hasCoOwner,
        claimName: buildingUnitInfo.applicantName || "",
        claimApartmentBlock: buildingUnitInfo.building || "",
        claimApartmentUnit: buildingUnitInfo.unit || "",
      };

      const response = await axios.post(
        `${API_ENDPOINTS.BASE_URL}/mobile/claim`,
        claimData,
      );

      // 쿠키에 공동소유자 여부 정보 저장
      const updatedUserInfo = {
        ...userInfo,
        hasCoOwner: hasCoOwner,
      };

      Cookies.set(COOKIE_KEYS.USER_INFO, JSON.stringify(updatedUserInfo), {
        expires: COOKIE_EXPIRES.USER_INFO,
        ...COOKIE_OPTIONS,
      });

      // API 호출 성공 후 페이지 이동
      navigate("/applicant-info-v2", {
        state: {
          building: buildingUnitInfo.building || "102",
          unit: buildingUnitInfo.unit || "0000",
          hasCoOwner: hasCoOwner,
        },
      });
    } catch (error) {
      console.error("Claim API 호출 실패:", error);
      // API 호출 실패 시에도 페이지 이동 (사용자 경험을 위해)
      const cookieData = Cookies.get("buildingUnitInfo");
      let buildingUnitInfo = {};
      if (cookieData) {
        try {
          buildingUnitInfo = JSON.parse(cookieData);
        } catch (error) {
          console.error("쿠키 파싱 오류:", error);
        }
      }

      navigate("/applicant-info-v2", {
        state: {
          building: buildingUnitInfo.building || "102",
          unit: buildingUnitInfo.unit || "0000",
          hasCoOwner: hasCoOwner,
        },
      });
    }
  };

  return (
    <AnimatePresence mode="wait">
      {!showProcessingModal &&
      !showCompleteModal &&
      !showPartialCompleteModal ? (
        <motion.div
          className="page-container"
          variants={pageVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            minHeight: "100vh",
            padding: "0 0px",
            position: "relative",
            paddingBottom: "104px",
          }}
        >
          {/* 로딩 상태 표시 */}
          {isLoading && (
            <motion.div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 1000,
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              {/* <div
              style={{
                backgroundColor: "white",
                padding: "20px",
                borderRadius: "8px",
                textAlign: "center",
              }}
            >
              <div>로그인 처리 중...</div>
            </div> */}
            </motion.div>
          )}

          <motion.div
            style={{
              marginTop: "20px",
            }}
            className="page-header"
            initial={{ opacity: 0, y: 20 }}
            animate={{
              opacity: 1,
              y: 0,
              transition: {
                delay: 0.1,
                duration: 0.5,
                ease: [0.4, 0, 0.2, 1],
              },
            }}
          >
            <h1>공동소유자 확인</h1>
          </motion.div>

          <motion.div
            className="co-owner-check-content"
            variants={contentVariants}
            initial="initial"
            animate="animate"
          >
            <div className="question-section">
              <h2
                style={{
                  fontSize: "22px",
                  fontWeight: "700",
                  color: "#2E3840",
                  marginBottom: "16px",
                }}
              >
                공동소유자 여부 확인
              </h2>
              <h3
                style={{
                  fontSize: "16px",
                  fontWeight: "500",
                  color: "#4B5563",
                  lineHeight: "1.6",
                }}
              >
                이 부동산에 귀하 외에 공동소유자가 있습니까?
              </h3>
              <p
                style={{
                  fontSize: "16px",
                  fontWeight: "500",
                  color: "#4B5563",
                  marginBottom: "24px",
                }}
              >
                공동소유자가 있는 경우 전자서명이 필요합니다.
              </p>

              <div
                style={{
                  marginTop: "60px",
                }}
                className="options-container"
              >
                <button
                  className={`option-button ${
                    hasCoOwner === true ? "selected" : ""
                  }`}
                  onClick={() => handleCoOwnerSelection(true)}
                >
                  <div className="option-content">
                    <span>예</span>
                    <p>공동소유자가 있습니다.</p>
                  </div>
                  <img
                    src={
                      hasCoOwner === true
                        ? "/RadioButtonActive.png"
                        : "/RadioButtonInactive.png"
                    }
                    alt="radio button"
                    style={{ width: "24px", height: "24px" }}
                  />
                </button>

                <button
                  style={{
                    marginTop: "6px",
                  }}
                  className={`option-button ${
                    hasCoOwner === false ? "selected" : ""
                  }`}
                  onClick={() => handleCoOwnerSelection(false)}
                >
                  <div className="option-content">
                    <span>아니오</span>
                    <p>단독 소유입니다.</p>
                  </div>
                  <img
                    src={
                      hasCoOwner === false
                        ? "/RadioButtonActive.png"
                        : "/RadioButtonInactive.png"
                    }
                    alt="radio button"
                    style={{ width: "24px", height: "24px" }}
                  />
                </button>
              </div>
            </div>
          </motion.div>

          <motion.div
            style={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              padding: "24px",
              background: "white",
              display: "flex",
              justifyContent: "center",
              width: "100%",
            }}
            variants={buttonVariants}
            initial="initial"
            animate="animate"
          >
            <button
              className="confirm-button"
              onClick={handleNext}
              disabled={hasCoOwner === null}
              style={{
                width: "100%",
                maxWidth: "460px",
                height: "56px",
                fontSize: "16px",
                fontWeight: "600",
                background: hasCoOwner === null ? "#EBEBFF" : "#7A4FFF",
                color: hasCoOwner === null ? "#fff" : "#EBEBFF",
                border: "none",
                borderRadius: "8px",
                cursor: hasCoOwner === null ? "not-allowed" : "pointer",
                transition: "background-color 0.2s ease",
                // boxShadow:
                //   hasCoOwner === null
                //     ? "none"
                //     : "0 2px 5px rgba(0, 117, 255, 0.2)",
              }}
            >
              다음
            </button>
          </motion.div>
        </motion.div>
      ) : null}

      {/* 완료 모달 */}
      {showCompleteModal && (
        <motion.div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 1)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "24px",
              width: "90%",
              maxWidth: "360px",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
          >
            <div style={{ textAlign: "center", marginBottom: "20px" }}>
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#1F2937",
                  margin: "0 0 8px 0",
                }}
              >
                이미 완료된 신청
              </h3>
              <p
                style={{
                  fontSize: "14px",
                  color: "#6B7280",
                  margin: 0,
                  lineHeight: "1.5",
                }}
              >
                이미 채권양도양수신청이 완료되었습니다.
              </p>
            </div>
            <button
              onClick={() => {
                setShowCompleteModal(false);
                navigate("/submission-result");
              }}
              style={{
                width: "100%",
                padding: "12px",
                backgroundColor: "#7A4FFF",
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontSize: "16px",
                fontWeight: "500",
                cursor: "pointer",
              }}
            >
              접수내역 확인하기
            </button>
          </motion.div>
        </motion.div>
      )}

      {/* 진행중인 신청 모달 */}
      {showProcessingModal && processingClaimData && (
        <motion.div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 1)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "24px",
              width: "90%",
              maxWidth: "360px",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
          >
            <div style={{ textAlign: "center", marginBottom: "20px" }}>
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#1F2937",
                  margin: "0 0 8px 0",
                }}
              >
                진행중인 신청건이 있습니다
              </h3>
              <div
                style={{
                  backgroundColor: "#F3F4F6",
                  borderRadius: "8px",
                  padding: "16px",
                  margin: "16px 0",
                  textAlign: "left",
                }}
              >
                <p
                  style={{
                    fontSize: "14px",
                    color: "#374151",
                    margin: "0 0 4px 0",
                    fontWeight: "500",
                  }}
                >
                  {formatBuildingLabel(
                    processingClaimData.claimApartmentBlock,
                    Cookies.get("apartmentCode"),
                  )}{" "}
                  {processingClaimData.claimApartmentUnit}호
                </p>
                <p
                  style={{
                    fontSize: "14px",
                    color: "#6B7280",
                    margin: 0,
                  }}
                >
                  신청인: {processingClaimData.claimAssignmentName}
                </p>
              </div>
              <p
                style={{
                  fontSize: "14px",
                  color: "#6B7280",
                  margin: 0,
                  lineHeight: "1.5",
                }}
              >
                기존 신청을 삭제하고 새로 진행하시겠습니까?
              </p>
            </div>
            <button
              onClick={handleDeleteClaim}
              style={{
                width: "100%",
                padding: "12px",
                backgroundColor: "#EF4444",
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontSize: "16px",
                fontWeight: "500",
                cursor: "pointer",
                marginBottom: "8px",
              }}
            >
              신청서 삭제하고 다시 진행
            </button>
            <button
              onClick={() => navigate("/")}
              style={{
                width: "100%",
                padding: "12px",
                backgroundColor: "#F3F4F6",
                color: "#374151",
                border: "none",
                borderRadius: "8px",
                fontSize: "16px",
                fontWeight: "500",
                cursor: "pointer",
              }}
            >
              취소
            </button>
          </motion.div>
        </motion.div>
      )}

      {/* 부분 완료 모달 */}
      {showPartialCompleteModal && partialCompleteData && (
        <motion.div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 1)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "24px",
              width: "90%",
              maxWidth: "360px",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              maxHeight: "80vh",
              overflow: "auto",
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
          >
            <div style={{ textAlign: "center", marginBottom: "20px" }}>
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#1F2937",
                  margin: "0 0 8px 0",
                }}
              >
                신청 진행 상황
              </h3>
              <div
                style={{
                  backgroundColor: "#F3F4F6",
                  borderRadius: "8px",
                  padding: "16px",
                  margin: "16px 0",
                  textAlign: "left",
                }}
              >
                <p
                  style={{
                    fontSize: "14px",
                    color: "#374151",
                    margin: "0 0 4px 0",
                    fontWeight: "500",
                  }}
                >
                  {formatBuildingLabel(
                    partialCompleteData.claimApartmentBlock,
                    Cookies.get("apartmentCode"),
                  )}{" "}
                  {partialCompleteData.claimApartmentUnit}호
                </p>
                <p
                  style={{
                    fontSize: "14px",
                    color: "#6B7280",
                    margin: 0,
                  }}
                >
                  신청인: {partialCompleteData.claimAssignmentName}
                </p>
              </div>
              <p
                style={{
                  fontSize: "14px",
                  color: "#6B7280",
                  margin: "0 0 16px 0",
                  lineHeight: "1.5",
                }}
              >
                공동신청자의 진행 상황을 확인해주세요.
              </p>
            </div>

            {/* 공동신청자 목록 */}
            <div style={{ marginBottom: "20px" }}>
              <h4
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#1F2937",
                  margin: "0 0 12px 0",
                }}
              >
                공동신청자 현황
              </h4>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                {partialCompleteData.claimShareList.map((share, index) => (
                  <div
                    key={share.claimAssignmentShareId}
                    style={{
                      backgroundColor: "#F9FAFB",
                      borderRadius: "8px",
                      padding: "12px",
                      border: "1px solid #E5E7EB",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "4px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#1F2937",
                        }}
                      >
                        {share.shareName}
                      </span>
                      <span
                        style={{
                          fontSize: "12px",
                          color:
                            share.claimAgree && share.claimIdCard
                              ? "#10B981"
                              : "#EF4444",
                          fontWeight: "500",
                        }}
                      >
                        {share.claimAgree && share.claimIdCard
                          ? "완료"
                          : "진행중"}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "2px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: "12px",
                        }}
                      >
                        <span style={{ color: "#6B7280" }}>동의 여부:</span>
                        <span
                          style={{
                            color: share.claimAgree ? "#10B981" : "#EF4444",
                            fontWeight: "500",
                          }}
                        >
                          {share.claimAgree ? "동의" : "미동의"}
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: "12px",
                        }}
                      >
                        <span style={{ color: "#6B7280" }}>신분증 제출:</span>
                        <span
                          style={{
                            color: share.claimIdCard ? "#10B981" : "#EF4444",
                            fontWeight: "500",
                          }}
                        >
                          {share.claimIdCard ? "완료" : "미완료"}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <button
              onClick={() => {
                setShowPartialCompleteModal(false);
                navigate("/submission-result");
              }}
              style={{
                width: "100%",
                padding: "12px",
                backgroundColor: "#7A4FFF",
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontSize: "16px",
                fontWeight: "500",
                cursor: "pointer",
              }}
            >
              신청내역 확인하기
            </button>
          </motion.div>
        </motion.div>
      )}

      {/* 에러 모달 */}
      {showErrorModal && (
        <motion.div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 1)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="error-modal"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
          >
            <p>{loginError}</p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default CoOwnerCheckPage;
